import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import {BaseFormComponent} from '../../shared/baseFormComponent';
import {InfoItem} from '../AccountInfo/infoitem';
import {AccountComponent} from '../AccountsListComponent/account';
import {PageHeader} from '../../shared/PageHeader';
import {DateTimePicker} from '../../shared/TimePicker';
import {MakeApiCall, WriteConsoleErrors, GetMomentTimeZoneName, GoBackOrRoot} from '../../shared/utils';

import './styles.scss';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';

const ADDPAYMENT_ACTION = {
    NONE: 0,
    SAVE: 1
}

export class AddAccountPaymentFormComponent extends BaseFormComponent {
    constructor(props, ...others) {
        super(props, ...others);

        const paymentType = 
            props.paymentTypes.find(p => p.Id == props.paymentTypeId) ||
            (props.paymentTypes.length > 0 ? props.paymentTypes[0] : null);

        this.state = {
            errors: null,
            action: ADDPAYMENT_ACTION.NONE,
            formData: {
                PaymentTypeId: Boolean(paymentType) ? paymentType.Id : '',
                TransactionId: '',
                CreatedAt: moment.utc(),
                Amount: 0,
                Fee: 0,
                AdminNotes: ''
            }
        }

        this._valueHandler.onChangeOptions = {
            'TransactionId': {doLTrim: true},
            'Amount': {isNumber: true, precision: 2},
            'Fee': {isNumber: true, min: 0, precision: 2},
        }
        this._valueHandler.onBlurOptions = {
            'TransactionId': {doLTrim: true, doRTrim: true},
        }

        this.handleDateTimeChange = this.handleDateTimeChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.doSave = this.doSave.bind(this);
        this.doCancel = this.doCancel.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleDateTimeChange(name, value) {
        const self = this;
        const formData = {...self.state.formData};

        const v = moment.tz(value.format('YYYY-MM-DDTHH:mm:ss'), self.props.timeZoneId);

        formData[name] = moment.utc(v);

        self.setState({formData: formData});
    }

    handleSubmit(e) {
        e.preventDefault();
        this.doSave();
    }

    handleCancelClick() {
        this.doCancel();
    }

    doSave() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const {verificationToken, accountId} = self.props;
        const {formData} = self.state;

        const data = {
            PaymentTypeId: formData.PaymentTypeId,
            AccountId: accountId,
            TransactionId: formData.TransactionId,
            Amount: formData.Amount,
            Fee: formData.Fee,
            AdminNotes: formData.AdminNotes,
            CreatedAt: formData.CreatedAt.format(),
            Description: formData.Description
        }

        new Promise(resolve => {
            self.setState({action: ADDPAYMENT_ACTION.SAVE}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.post(
                        {
                            url: '/api/admin/addAccountPayment',
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    )
                )
            }
            return null;
        })
        .then(self.doCancel)
        .catch(errors => {
            if (self._isMounted) {
                self.setState({action: ADDPAYMENT_ACTION.NONE, errors: errors});
                WriteConsoleErrors(errors, true, true);
            }
        });
    }

    doCancel() {
        if (this._isMounted) {
            GoBackOrRoot();
        }
    }

    render() {
        const self = this;

        const {paymentTypes, accountId, accountName, timeZoneId} = self.props;
        const {action, formData, errors} = self.state;

        const isDisabled = action != ADDPAYMENT_ACTION.NONE;

        const paymentType = paymentTypes.find(p => p.Id == formData.PaymentTypeId);
        const createdAt = moment(formData.CreatedAt).isValid() ? moment(formData.CreatedAt).tz(timeZoneId) : moment.utc();

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={75}
                    caption={
                        <h2 className="text-truncate">Add payment for account</h2>
                    }
                />

                <div className="form-group">
                    <InfoItem caption="Account" content={<AccountComponent id={accountId} name={accountName} disabled={isDisabled} showId showLoginAs linkToInfo />} />
                </div>

                <form onSubmit={self.handleSubmit}>
                    <div className="row">
                        <div className="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                            <div className="form-group newstyle">
                                <select
                                    id="PaymentTypeId"
                                    name="PaymentTypeId"
                                    className={self.getControlClass('PaymentTypeId', errors, 'custom-select' + (Boolean(paymentType) ? '' : ' is-invalid'))}
                                    disabled={isDisabled}
                                    value={Boolean(paymentType) ? paymentType.Id : ''}
                                    required
                                    onChange={self.handleInputChange}
                                >
                                    {!Boolean(paymentType) && <option key={-1} value="" disabled hidden />}
                                    {paymentTypes
                                        .map(p => {
                                            return <option key={'pt-' + p.Id} value={p.Id}>{p.Name}</option>
                                        })
                                    }
                                </select>
                                <label htmlFor="PaymentTypeId"><span className="text-truncate">Payment type</span></label>
                                <div className="invalid-feedback">{self.getControlError('PaymentTypeId', errors)}</div>
                            </div>

                            <div className="form-group newstyle">
                                <input
                                    id="TransactionId"
                                    name="TransactionId"
                                    type="text"
                                    className={self.getControlClass('TransactionId', errors, 'form-control')}
                                    disabled={isDisabled}
                                    value={formData.TransactionId}
                                    onChange={self.handleInputChange}
                                    onBlur={self.handleInputChange}
                                />
                                <label htmlFor="TransactionId"><span className="text-truncate">Transaction #</span></label>
                                <div className="invalid-feedback">{self.getControlError('TransactionId', errors)}</div>
                            </div>

                            <div className="form-group newstyle">
                                <DateTimePicker
                                    inputProps={{className: 'custom-select w-100', id: 'CreatedAt'}}
                                    disabled={isDisabled}
                                    value={createdAt}
                                    onChange={value => self.handleDateTimeChange('CreatedAt', value)}
                                />
                                <label htmlFor="CreatedAt">
                                    <span className="text-truncate">
                                        Payment time
                                        <span className="ml-1 small text-secondary">{GetMomentTimeZoneName(timeZoneId, GetMomentTimeZoneName.FLAG_ADD_UTCOFFSET, '')}</span>
                                    </span>
                                </label>
                            </div>

                            <div className="d-flex">
                                <div className="form-group newstyle mr-2">
                                    <input
                                        id="Amount"
                                        name="Amount"
                                        type="number"
                                        className={self.getControlClass('Amount', errors, 'form-control text-right')}
                                        disabled={isDisabled}
                                        value={formData.Amount}
                                        onChange={self.handleInputChange}
                                        onBlur={self.handleInputChange}
                                        style={{width: '7.5em'}}
                                    />
                                    <label htmlFor="Amount"><span className="text-truncate">Amount, $</span></label>
                                    <div className="invalid-feedback">{self.getControlError('Amount', errors)}</div>
                                </div>
                                <div className="form-group newstyle">
                                    <input
                                        id="Fee"
                                        name="Fee"
                                        type="number"
                                        className={self.getControlClass('Fee', errors, 'form-control text-right')}
                                        disabled={isDisabled}
                                        value={formData.Fee}
                                        onChange={self.handleInputChange}
                                        onBlur={self.handleInputChange}
                                        style={{width: '5.5em'}}
                                    />
                                    <label htmlFor="Fee"><span className="text-truncate">Fee, $</span></label>
                                    <div className="invalid-feedback">{self.getControlError('Fee', errors)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-10 col-lg-7 col-xl-6">
                            <div className="form-group newstyle">
                                <input
                                    id="Description"
                                    name="Description"
                                    type="text"
                                    className={self.getControlClass('Description', errors, 'form-control')}
                                    disabled={isDisabled}
                                    value={formData.Description}
                                    onChange={self.handleInputChange}
                                    onBlur={self.handleInputChange}
                                />
                                <label htmlFor="Description">
                                    <span className="text-truncate">
                                        Description
                                        <span className="ml-1 small text-secondary">Leave blank for automatic creation</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-10 col-lg-7 col-xl-6">
                            <div className="form-group newstyle">
                                <textarea
                                    id="AdminNotes"
                                    name="AdminNotes"
                                    className="form-control mw-100"
                                    rows={3}
                                    disabled={isDisabled}
                                    value={formData.AdminNotes || ''}
                                    onChange={self.handleInputChange}
                                />
                                <label htmlFor="AdminNotes"><span className="text-truncate">Notes</span></label>
                            </div>
                        </div>
                    </div>

                    <ErrorsBlockComponent errors={errors} excludedErrors="PaymentTypeId TransactionId Amount Fee" unstyled />

                    <div className="form-buttons pt-3">
                        <button
                            type="submit"
                            disabled={isDisabled}
                            className="btn btn-sm btn-primary newstyle"
                        >
                            Add payment
                            <ButtonBusyIndicatorComponent visible={action == ADDPAYMENT_ACTION.SAVE} />
                        </button>
                        <button
                            type="button"
                            disabled={isDisabled}
                            className="btn btn-sm btn-outline-secondary newstyle"
                            onClick={self.handleCancelClick}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        )
    }
}
AddAccountPaymentFormComponent.propTypes = {
    accountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    accountName: PropTypes.string.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    timeZoneName: PropTypes.string,
    paymentTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired}))
}